import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Plus,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  GitCompare,
} from "lucide-react";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";
import {
  COMPARE_STATS,
  hexLuminance,
  aggregateStats,
  fetchSeasonStats,
  fetchPlayerProfile,
} from "./comparisonHelpers";

// ─── JerseyAvatar SVG (mirrors Home.jsx) ─────────────────────────────────────
function JerseyAvatar({ team, number, size }) {
  const sz = size || 52;
  const ts = getTeamStyle(team || {});
  const primary = ts.primaryHex;
  const secondary = ts.accentHex;
  const lum = hexLuminance(primary);
  const numColor = lum < 0.35 ? "#FFF" : "#000";
  const collar = lum < 0.35 ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)";
  const uid = "cmp" + primary.replace("#", "");
  return (
    <div
      style={{
        width: sz,
        height: sz,
        flexShrink: 0,
        filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.45))",
      }}
      aria-hidden="true"
    >
      <svg
        width={sz}
        height={sz}
        viewBox="0 0 44 50"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id={uid + "p"}
            width="4"
            height="4"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 0 2 L 4 2 M 2 0 L 2 4"
              stroke="#000"
              strokeWidth="0.4"
              opacity="0.8"
            />
            <path
              d="M 0 0 L 2 2 L 4 0 M 0 4 L 2 2 L 4 4"
              stroke="#000"
              strokeWidth="0.5"
              fill="none"
            />
            <path d="M 0 2 L 2 0 L 4 2 L 2 4 Z" fill="#FFF" opacity="0.15" />
          </pattern>
          <clipPath id={uid + "c"}>
            <path d="M 12 4 L 16 4 Q 22 14 28 4 L 32 4 Q 30 12 38 20 L 38 45 Q 38 48 35 48 L 9 48 Q 6 48 6 45 L 6 20 Q 14 12 12 4 Z" />
          </clipPath>
        </defs>
        <path
          d="M 12 4 L 16 4 Q 22 14 28 4 L 32 4 Q 30 12 38 20 L 38 45 Q 38 48 35 48 L 9 48 Q 6 48 6 45 L 6 20 Q 14 12 12 4 Z"
          fill={primary}
          stroke={secondary}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <g clipPath={"url(#" + uid + "c)"}>
          <path d="M 6 20 L 11 20 L 11 48 L 6 48 Z" fill="#FFF" />
          <path
            d="M 6 20 L 11 20 L 11 48 L 6 48 Z"
            fill={"url(#" + uid + "p)"}
          />
          <line
            x1="11"
            y1="20"
            x2="11"
            y2="48"
            stroke="#000"
            strokeWidth="0.8"
          />
          <path d="M 33 20 L 38 20 L 38 48 L 33 48 Z" fill="#FFF" />
          <path
            d="M 33 20 L 38 20 L 38 48 L 33 48 Z"
            fill={"url(#" + uid + "p)"}
          />
          <line
            x1="33"
            y1="20"
            x2="33"
            y2="48"
            stroke="#000"
            strokeWidth="0.8"
          />
        </g>
        <path
          d="M 12 4 Q 14 12 6 20"
          fill="none"
          stroke={secondary}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M 32 4 Q 30 12 38 20"
          fill="none"
          stroke={secondary}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M 16 4 Q 22 14 28 4"
          fill="none"
          stroke={secondary}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M 16.5 5.2 Q 22 14.5 27.5 5.2"
          fill="none"
          stroke={collar}
          strokeWidth="1"
          strokeLinecap="round"
        />
        {number != null && number !== "" && (
          <text
            x="22"
            y="32"
            textAnchor="middle"
            dominantBaseline="central"
            fill={numColor}
            fontFamily="'Oswald', sans-serif"
            fontWeight="900"
            fontSize={String(number).length > 1 ? "13" : "16"}
          >
            {number}
          </text>
        )}
      </svg>
    </div>
  );
}

// ─── PlayerAvatar — profile pic or jersey fallback ───────────────────────────
function PlayerAvatar({ avatarUrl, avatarCrop, team, jerseyNumber, size }) {
  const sz = size || 52;
  if (avatarUrl) {
    const crop = avatarCrop || {};
    const zoom = crop.zoom || 1;
    const ox = crop.offsetX || 0;
    const oy = crop.offsetY || 0;
    const tf =
      "scale(" + zoom + ") translate(" + ox * 100 + "%, " + oy * 100 + "%)";
    return (
      <div
        style={{
          width: sz,
          height: sz,
          flexShrink: 0,
          position: "relative",
          overflow: "hidden",
          borderRadius: "50%",
          boxShadow: "0 0 0 2px rgba(255,255,255,0.3)",
        }}
      >
        <img
          src={avatarUrl}
          alt="Profile"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: tf,
            transformOrigin: "center center",
          }}
        />
      </div>
    );
  }
  return <JerseyAvatar team={team} number={jerseyNumber} size={sz} />;
}

// ─── PlayerSearchDropdown ─────────────────────────────────────────────────────
// NOTE: The dropdown is rendered via a fixed-position portal so it escapes any
// parent overflow:hidden containers (the rounded card clips it otherwise).
function PlayerSearchDropdown({ players, value, onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const h = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropRef.current &&
        !dropRef.current.contains(e.target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Reposition dropdown whenever it opens
  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen((o) => !o);
    setQuery("");
  };

  const filtered = players
    .filter((p) => {
      const q = query.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        (p.team || "").toLowerCase().includes(q)
      );
    })
    .slice(0, 30);
  const selected = players.find((p) => p.id === value);

  // Portal dropdown — renders at document.body to escape overflow:hidden parents
  const portalDropdown = open
    ? createPortal(
        <div
          ref={dropRef}
          style={{
            position: "fixed",
            top: dropPos.top,
            left: dropPos.left,
            width: dropPos.width,
            zIndex: 9999,
          }}
          className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden"
        >
          <div className="p-2 border-b border-gray-100 dark:border-gray-800">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">
                No players found
              </div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onChange(p.id);
                    setOpen(false);
                  }}
                  className={
                    "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-orange-50 dark:hover:bg-orange-900/20 transition" +
                    (p.id === value
                      ? " bg-orange-50 dark:bg-orange-900/20"
                      : "")
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-800 dark:text-white truncate">
                      {p.name}
                    </div>
                    <div className="text-[10px] text-gray-400 truncate">
                      #{p.jersey} · {p.team}
                    </div>
                  </div>
                  {p.id === value && (
                    <span className="text-orange-500 text-xs font-bold flex-shrink-0">
                      ✓
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={triggerRef}>
      <button
        type="button"
        onClick={handleOpen}
        className="w-full flex items-center gap-1.5 px-2.5 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left hover:border-orange-400 transition"
      >
        <span className="flex-1 truncate text-xs font-medium text-gray-800 dark:text-gray-200">
          {selected ? (
            selected.name
          ) : (
            <span className="text-gray-400">
              {placeholder || "Pick player…"}
            </span>
          )}
        </span>
        <ChevronDown size={11} className="text-gray-400 flex-shrink-0" />
      </button>
      {portalDropdown}
    </div>
  );
}

// ─── ColumnHeader card ────────────────────────────────────────────────────────
function ColumnHeader({
  col,
  colIndex,
  players,
  allPlayers,
  seasons,
  onPlayerChange,
  onSeasonChange,
  onGameTypeChange,
  onRemove,
  canRemove,
}) {
  // `players` is the per-season list; `allPlayers` is the fallback
  const playerList = players || allPlayers || [];
  const player = playerList.find((p) => p.id === col.playerId);
  const teamObj = player
    ? {
        primary_color: player.team_primary_color,
        secondary_color: player.team_secondary_color,
        gradient: player.team_gradient,
      }
    : {};
  const ts = getTeamStyle(teamObj);
  const isDark = isDarkModeActive();
  const htc = getTextColorForAccent(ts.primaryHex, isDark);
  const isLight = htc === "#ffffff";
  // Type-safe season lookup: s.id may be integer, col.seasonId may be string
  const seasonObj = seasons.find((s) => String(s.id) === String(col.seasonId));
  const seasonName = seasonObj ? seasonObj.name : null;
  return (
    <div className="rounded-2xl overflow-hidden shadow-md border border-gray-200 dark:border-gray-700 w-full">
      <div
        className="px-2 pt-2.5 pb-1.5 relative"
        style={{ ...ts.headerStyle, color: htc }}
      >
        {canRemove && (
          <button
            onClick={() => onRemove(colIndex)}
            className="absolute top-1.5 right-1.5 p-0.5 rounded-full hover:bg-white/20 transition"
            aria-label="Remove column"
          >
            <X size={11} />
          </button>
        )}
        <div className="flex justify-center mb-1.5">
          {col.avatarLoading ? (
            <div
              className="rounded-full bg-white/20 animate-pulse"
              style={{ width: 40, height: 40 }}
            />
          ) : (
            <PlayerAvatar
              avatarUrl={col.avatarUrl}
              avatarCrop={col.avatarCrop}
              team={teamObj}
              jerseyNumber={player ? player.jersey : null}
              size={40}
            />
          )}
        </div>
        <div className="text-center">
          <div
            className="text-[11px] font-black leading-tight truncate"
            style={{ color: htc }}
          >
            {player ? (
              player.name
            ) : (
              <span style={{ opacity: 0.5 }}>Select player</span>
            )}
          </div>
          {player && (
            <div
              className="text-[9px] font-semibold mt-0.5 truncate"
              style={{
                color: isLight ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.5)",
              }}
            >
              #{player.jersey} · {player.team}
            </div>
          )}
        </div>
        {/* Always show season label when a season is selected, even for inactive seasons */}
        {col.seasonId && (
          <div
            className="text-center text-[9px] font-bold mt-1 truncate"
            style={{
              color: isLight ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
            }}
          >
            {seasonName || "Season " + col.seasonId} ·{" "}
            {col.gameType === "playoff" ? "P/O" : "R"}
          </div>
        )}
      </div>
      <div className="bg-white dark:bg-gray-900 p-1.5 space-y-1">
        <PlayerSearchDropdown
          players={playerList}
          value={col.playerId}
          onChange={(id) => onPlayerChange(colIndex, id)}
          placeholder="Pick player…"
        />
        <select
          value={col.seasonId != null ? String(col.seasonId) : ""}
          onChange={(e) => onSeasonChange(colIndex, e.target.value)}
          className="w-full px-2 py-1 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-[10px] font-semibold text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="">Season…</option>
          {seasons.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </select>
        <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 text-[10px] font-bold">
          {["regular", "playoff"].map((gt) => (
            <button
              key={gt}
              onClick={() => onGameTypeChange(colIndex, gt)}
              className={
                "flex-1 py-1 transition " +
                (col.gameType === gt
                  ? "bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900"
                  : "bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700")
              }
            >
              {gt === "regular" ? "Regular" : "Playoffs"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── StatRow with color-coded leader highlighting ─────────────────────────────
function StatRow({ stat, columns }) {
  const values = columns.map((col) => {
    if (!col.stats) return null;
    const v = col.stats[stat.key];
    return v != null ? v : null;
  });
  const valid = values.filter((v) => v !== null);
  if (valid.length === 0) {
    return (
      <tr className="border-b border-gray-100 dark:border-gray-800">
        <td className="py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 sticky left-0 bg-white dark:bg-gray-900 z-10 whitespace-nowrap">
          {stat.label}{" "}
          <span className="text-[10px] text-gray-400 font-normal">
            ({stat.unit})
          </span>
        </td>
        {columns.map((_, i) => (
          <td
            key={i}
            className="py-2.5 px-3 text-center text-xs text-gray-300 dark:text-gray-600"
          >
            —
          </td>
        ))}
      </tr>
    );
  }
  const maxVal = Math.max(...valid);
  const minVal = Math.min(...valid);
  const allSame = maxVal === minVal;
  return (
    <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
      <td className="py-2.5 px-3 text-xs font-semibold text-gray-600 dark:text-gray-400 sticky left-0 bg-white dark:bg-gray-900 z-10 whitespace-nowrap">
        {stat.label}{" "}
        <span className="text-[10px] text-gray-400 font-normal">
          ({stat.unit})
        </span>
      </td>
      {values.map((val, i) => {
        if (val === null) {
          return (
            <td
              key={i}
              className="py-2.5 px-3 text-center text-xs text-gray-300 dark:text-gray-600"
            >
              —
            </td>
          );
        }
        const isLeader =
          !allSame && (stat.higherIsBetter ? val === maxVal : val === minVal);
        const isTrailer =
          !allSame && (stat.higherIsBetter ? val === minVal : val === maxVal);
        const bg = isLeader
          ? "bg-emerald-50 dark:bg-emerald-900/20"
          : isTrailer
            ? "bg-red-50 dark:bg-red-900/10"
            : "";
        const tc = isLeader
          ? "text-emerald-700 dark:text-emerald-400"
          : isTrailer
            ? "text-red-500 dark:text-red-400"
            : "text-gray-800 dark:text-gray-200";
        const icon = isLeader ? (
          <ArrowUp size={10} className="inline ml-0.5 text-emerald-500" />
        ) : isTrailer ? (
          <ArrowDown size={10} className="inline ml-0.5 text-red-400" />
        ) : null;
        const display = stat.isCount ? String(val) : val.toFixed(1);
        return (
          <td
            key={i}
            className={
              "py-2.5 px-3 text-center text-sm font-black tabular-nums " +
              bg +
              " " +
              tc
            }
          >
            {display}
            {icon}
          </td>
        );
      })}
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PlayerComparisonPanel({
  isOpen,
  onClose,
  initialPlayers,
  allPlayers,
  seasons,
  defaultSeasonId,
}) {
  const _initialPlayers = initialPlayers || [];
  // allPlayers from Stats page is only used as a fallback seed; the panel
  // builds its own per-season player lists from the season cache.
  const _seasons = seasons || [];
  const _defaultSeasonId = defaultSeasonId || null;

  const [columns, setColumns] = useState([]);
  // Per-season aggregated player lists (independent of Stats page season)
  const [seasonPlayers, setSeasonPlayers] = useState({}); // { seasonId: Player[] }
  const seasonCache = useRef({});
  const profileCache = useRef({});

  // ── Initialise columns fresh every time the panel opens ───────────────────
  // We intentionally re-init on every open so that new initialPlayers are
  // picked up and stats are computed immediately (fixes Bug 4).
  useEffect(() => {
    if (!isOpen) return;
    const seed = _initialPlayers.slice(0, 4);
    const makeCol = (pid) => ({
      playerId: pid || null,
      seasonId: _defaultSeasonId,
      gameType: "regular",
      stats: null,
      avatarUrl: null,
      avatarCrop: null,
      avatarLoading: false,
    });
    const initial =
      seed.length >= 2
        ? seed.map((p) => makeCol(p.id))
        : [makeCol(null), makeCol(null)];
    setColumns(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Fetch season data (cached) + build independent player list ────────────
  const getSeasonData = useCallback(async (seasonId) => {
    if (!seasonId) return [];
    if (seasonCache.current[seasonId]) return seasonCache.current[seasonId];
    const rows = await fetchSeasonStats(seasonId);
    seasonCache.current[seasonId] = rows;
    return rows;
  }, []);

  // Build a deduplicated, sorted player list for a given season from the cache.
  // This is what the search dropdowns use — fully independent of Stats page season.
  // Always use String(seasonId) as the key to avoid integer vs string mismatches.
  const buildSeasonPlayerList = useCallback(
    async (seasonId) => {
      if (!seasonId) return;
      const key = String(seasonId);
      if (seasonPlayers[key]) return; // already built
      const rows = await getSeasonData(seasonId);
      // Aggregate both regular + playoff to get all players who appeared that season
      const allAgg = [
        ...aggregateStats(rows, "regular"),
        ...aggregateStats(rows, "playoff"),
      ];
      const seen = new Set();
      const list = [];
      for (const p of allAgg) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          list.push(p);
        }
      }
      list.sort((a, b) => a.name.localeCompare(b.name));
      setSeasonPlayers((prev) => ({ ...prev, [key]: list }));
    },
    [getSeasonData, seasonPlayers],
  );

  // ── Fetch player profile (cached) ─────────────────────────────────────────
  const getPlayerProfile = useCallback(async (playerId) => {
    if (!playerId) return null;
    if (Object.prototype.hasOwnProperty.call(profileCache.current, playerId)) {
      return profileCache.current[playerId];
    }
    const profile = await fetchPlayerProfile(playerId);
    profileCache.current[playerId] = profile;
    return profile;
  }, []);

  // ── Recompute stats for a single column ───────────────────────────────────
  const recomputeColumn = useCallback(
    async (colIndex, col) => {
      if (!col.playerId || !col.seasonId) {
        setColumns((prev) => {
          const n = [...prev];
          if (n[colIndex]) n[colIndex] = { ...n[colIndex], stats: null };
          return n;
        });
        return;
      }
      const rows = await getSeasonData(col.seasonId);
      const agg = aggregateStats(rows, col.gameType);
      const ps = agg.find((p) => p.id === col.playerId) || null;
      setColumns((prev) => {
        const n = [...prev];
        if (n[colIndex]) n[colIndex] = { ...n[colIndex], stats: ps };
        return n;
      });
    },
    [getSeasonData],
  );

  // ── Fetch avatar for a column ─────────────────────────────────────────────
  const doFetchAvatar = useCallback(
    async (colIndex, playerId) => {
      if (!playerId) {
        setColumns((prev) => {
          const n = [...prev];
          if (n[colIndex])
            n[colIndex] = {
              ...n[colIndex],
              avatarUrl: null,
              avatarCrop: null,
              avatarLoading: false,
            };
          return n;
        });
        return;
      }
      setColumns((prev) => {
        const n = [...prev];
        if (n[colIndex]) n[colIndex] = { ...n[colIndex], avatarLoading: true };
        return n;
      });
      const profile = await getPlayerProfile(playerId);
      setColumns((prev) => {
        const n = [...prev];
        if (n[colIndex]) {
          n[colIndex] = {
            ...n[colIndex],
            avatarUrl: profile ? profile.avatar_url : null,
            avatarCrop: profile ? profile.avatar_crop : null,
            avatarLoading: false,
          };
        }
        return n;
      });
    },
    [getPlayerProfile],
  );

  // ── Seed stats + avatars once when the panel opens (columns just initialised)
  // We use a ref to track the columns snapshot at seed time so we can compare
  // against it on the next open. Individual mutations (player/season/gameType
  // changes) call recomputeColumn directly — they do NOT rely on this effect.
  const lastSeedKey = useRef("");
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  useEffect(() => {
    if (!isOpen) {
      // Reset seed key so next open always re-seeds even with same players
      lastSeedKey.current = "";
      return;
    }
    // Wait until columns have been initialised (non-empty)
    if (columnsRef.current.length === 0) return;

    const cols = columnsRef.current;
    const key = cols
      .map((c) => `${c.playerId}|${c.seasonId}|${c.gameType}`)
      .join(",");
    if (key === lastSeedKey.current) return;
    lastSeedKey.current = key;

    cols.forEach((col, i) => {
      recomputeColumn(i, col);
      if (col.playerId) doFetchAvatar(i, col.playerId);
    });
    // Pre-build player lists for each column's season
    const uniqueSeasons = [
      ...new Set(cols.map((c) => c.seasonId).filter(Boolean)),
    ];
    uniqueSeasons.forEach((sid) => buildSeasonPlayerList(sid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, columns]);

  // ── Column mutation handlers ───────────────────────────────────────────────
  const handlePlayerChange = useCallback(
    async (colIndex, playerId) => {
      // Capture the current column state BEFORE the setColumns call so we can
      // pass the correct seasonId/gameType to recomputeColumn without relying
      // on a stale closure inside a second setColumns callback.
      const currentCol = columns[colIndex];
      setColumns((prev) => {
        const n = [...prev];
        if (n[colIndex])
          n[colIndex] = { ...n[colIndex], playerId, stats: null };
        return n;
      });
      doFetchAvatar(colIndex, playerId);
      if (currentCol) {
        recomputeColumn(colIndex, { ...currentCol, playerId });
      }
    },
    [columns, doFetchAvatar, recomputeColumn],
  );

  const handleSeasonChange = useCallback(
    (colIndex, seasonId) => {
      // Pre-fetch player list for the newly selected season
      if (seasonId) buildSeasonPlayerList(seasonId);
      setColumns((prev) => {
        const n = [...prev];
        if (n[colIndex]) {
          const col = { ...n[colIndex], seasonId, stats: null };
          n[colIndex] = col;
          recomputeColumn(colIndex, col);
        }
        return n;
      });
    },
    [recomputeColumn, buildSeasonPlayerList],
  );

  const handleGameTypeChange = useCallback(
    (colIndex, gameType) => {
      setColumns((prev) => {
        const n = [...prev];
        if (n[colIndex]) {
          const col = { ...n[colIndex], gameType, stats: null };
          n[colIndex] = col;
          recomputeColumn(colIndex, col);
        }
        return n;
      });
    },
    [recomputeColumn],
  );

  const handleAddColumn = () => {
    if (columns.length >= 4) return;
    setColumns((prev) => [
      ...prev,
      {
        playerId: null,
        seasonId: _defaultSeasonId,
        gameType: "regular",
        stats: null,
        avatarUrl: null,
        avatarCrop: null,
        avatarLoading: false,
      },
    ]);
  };

  const handleRemoveColumn = (colIndex) => {
    if (columns.length <= 2) return;
    setColumns((prev) => prev.filter((_, i) => i !== colIndex));
  };

  const handleClearAll = () => {
    const makeCol = (pid) => ({
      playerId: pid || null,
      seasonId: _defaultSeasonId,
      gameType: "regular",
      stats: null,
      avatarUrl: null,
      avatarCrop: null,
      avatarLoading: false,
    });
    setColumns([makeCol(null), makeCol(null)]);
    lastSeedKey.current = "";
  };

  if (!isOpen) return null;

  const colCount = columns.length;

  return (
    <>
      {/* Backdrop — mobile only */}
      <div
        className="fixed inset-0 bg-black/40 z-40 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-label="Player Comparison"
        className="fixed z-50 bg-white dark:bg-gray-900 shadow-2xl flex flex-col
          bottom-0 left-0 right-0 rounded-t-3xl max-h-[90vh]
          lg:top-0 lg:right-0 lg:bottom-0 lg:left-auto lg:rounded-none lg:rounded-l-3xl lg:w-[680px] lg:max-h-screen
          border-t border-gray-200 dark:border-gray-700 lg:border-t-0 lg:border-l"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <GitCompare size={18} className="text-orange-500" />
            <div>
              <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wide">
                Player Comparison
              </h2>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                {colCount} column{colCount !== 1 ? "s" : ""} · up to 4
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {columns.length < 4 && (
              <button
                onClick={handleAddColumn}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-bold hover:bg-orange-100 dark:hover:bg-orange-900/40 transition"
              >
                <Plus size={12} /> Add
              </button>
            )}
            <button
              onClick={handleClearAll}
              className="px-3 py-1.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-xs font-bold hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400 transition"
              title="Clear all columns"
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition text-gray-500 dark:text-gray-400"
              aria-label="Close comparison panel"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Column headers — horizontal scroll on mobile, grid on desktop */}
          <div className="p-3 border-b border-gray-100 dark:border-gray-800">
            <div
              className="flex gap-2 overflow-x-auto pb-1 lg:overflow-x-visible lg:grid lg:gap-2"
              style={{
                gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
              }}
            >
              {columns.map((col, i) => {
                // Use the panel's own per-season player list; fall back to allPlayers prop
                const colPlayers =
                  col.seasonId && seasonPlayers[String(col.seasonId)]
                    ? seasonPlayers[String(col.seasonId)]
                    : allPlayers || [];
                return (
                  <div key={i} style={{ minWidth: 140 }} className="lg:min-w-0">
                    <ColumnHeader
                      col={col}
                      colIndex={i}
                      players={colPlayers}
                      allPlayers={colPlayers}
                      seasons={_seasons}
                      onPlayerChange={handlePlayerChange}
                      onSeasonChange={handleSeasonChange}
                      onGameTypeChange={handleGameTypeChange}
                      onRemove={handleRemoveColumn}
                      canRemove={columns.length > 2}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="px-4 py-2 flex items-center gap-4 text-[10px] font-semibold text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800">
            <span className="flex items-center gap-1">
              <ArrowUp size={10} className="text-emerald-500" />
              <span className="text-emerald-600 dark:text-emerald-400">
                Leader
              </span>
            </span>
            <span className="flex items-center gap-1">
              <ArrowDown size={10} className="text-red-400" />
              <span className="text-red-500 dark:text-red-400">Trailer</span>
            </span>
          </div>

          {/* Comparison table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <th className="py-2 px-3 text-left text-[10px] font-black uppercase text-gray-400 sticky left-0 bg-gray-50 dark:bg-gray-800/50 z-10">
                    Stat
                  </th>
                  {columns.map((col, i) => {
                    const colPlayers =
                      col.seasonId && seasonPlayers[String(col.seasonId)]
                        ? seasonPlayers[String(col.seasonId)]
                        : allPlayers || [];
                    const player =
                      colPlayers.find((p) => p.id === col.playerId) ||
                      (col.stats
                        ? { name: col.stats.name, jersey: col.stats.jersey }
                        : null);
                    // Type-safe lookup: s.id may be integer, col.seasonId may be string
                    const seasonObj = _seasons.find(
                      (s) => String(s.id) === String(col.seasonId),
                    );
                    const seasonName = seasonObj
                      ? seasonObj.name
                      : col.seasonId
                        ? "Season " + col.seasonId
                        : "—";
                    const gtSuffix =
                      col.gameType === "playoff" ? " · P/O" : " · R";
                    return (
                      <th
                        key={i}
                        className="py-2 px-2 text-center text-[10px] font-black uppercase text-gray-400 min-w-[70px]"
                      >
                        <div className="truncate">
                          {player
                            ? player.name.split(" ")[0]
                            : "Player " + (i + 1)}
                        </div>
                        <div className="text-[9px] font-normal text-gray-300 dark:text-gray-600 truncate">
                          {seasonName}
                          {gtSuffix}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {COMPARE_STATS.map((stat) => (
                  <StatRow key={stat.key} stat={stat} columns={columns} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty state */}
          {columns.every((c) => !c.stats) && (
            <div className="px-6 py-10 text-center">
              <div className="text-4xl mb-3">⚖️</div>
              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                Select players and seasons above to start comparing
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                You can compare the same player across different seasons
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
